import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import infoIcon from "../../assets/info.png";
// import './StudentEvaluationTemplate.scss';

const StudentEvaluationTemplate = forwardRef(({ evaluationData }, ref) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const schoolName = localStorage.getItem("organizationName");

  // Sample data for demonstration - replace with actual props
  const sampleData = {
    id: "",
    subjectEvaluation: {
      Math: "",
      Arabic: "",
      English: "",
    },
    behavior: "",
    specialCases: "",
    speaking: "",
    attentionAndFocus: "",
    notes: "",
    redFlag: true,
    studentId: "",
    student: {
      id: "",
      studentCode: "",
      fullName: "",
      notes: null,
      phoneNumber1: "",
      phoneNumber2: "",
      createdAt: "",
      updatedAt: "",
    },
  };

  const data = evaluationData || sampleData;

  const evaluationOptions = [
    { title: t("excellent"), value: "excellent" },
    { title: t("veryGood"), value: "veryGood" },
    { title: t("good"), value: "good" },
    { title: t("medium"), value: "medium" },
    { title: t("poor"), value: "poor" },
    { title: t("redoTest"), value: "redoTest" },
  ];

  return (
    <div
      ref={ref}
      className="sc-student-evaluation-container"
      dir={currentDirection}
      style={{
        display: "none",
      }}
    >
      {/* Header Section */}
      <header className="sc-header">
        <div className="sc-header-content">
          <h2 className="sc-title">{schoolName || "School of Thoughts"}</h2>
          <h4 className="sc-label">{t("studentEvaluation")}</h4>
        </div>

        {/* Student Info Bar */}
        <div className="sc-info-bar">
          <div>
            <span>{t("studentName")}:</span> {data.student.fullName}
          </div>
          <div>
            <span>{t("educationLevel")}:</span> {data.student.schoolClass?.name}
          </div>
          <div>
            <span>{t("studentId")}:</span> {data.student.studentCode}
          </div>
          <div>
            <span>{t("printDate")}:</span> {new Date().toLocaleDateString()}
          </div>
        </div>

        {/* Red Flag Indicator */}
        {data.redFlag && (
          <div className="sc-red-flag">
            <div className="sc-red-flag-indicator">
              <div className="sc-red-flag-circle"></div>
              <span className="sc-red-flag-text">
                {t("redFlag")}: {data.redFlag ? t("yes") : t("no")}
              </span>
            </div>
          </div>
        )}
      </header>

      {/* Body Section */}
      <main className="sc-body">
        {/* Academic Performance Section */}
        <section className="sc-section">
          <h3 className="sc-section-title">{t("level")}</h3>

          <div className="sc-subjects">
            {/* Math */}
            <div
              className={`sc-subject-row ${
                currentDirection == "rtl"
                  ? "sc-sections-border-right"
                  : "sc-sections-border-left"
              }`}
            >
              <div className="sc-subject-label">
                <span className="sc-subject-name">{t("math")}:</span>
              </div>
              <div className="sc-evaluation-options">
                {evaluationOptions.map((option) => (
                  <span
                    key={option.value}
                    className={`sc-evaluation-option ${
                      data.subjectEvaluation.Math === option.value
                        ? "sc-selected"
                        : ""
                    }`}
                  >
                    {option.title}
                  </span>
                ))}
              </div>
            </div>

            {/* English */}
            <div
              className={`sc-subject-row ${
                currentDirection == "rtl"
                  ? "sc-sections-border-right"
                  : "sc-sections-border-left"
              }`}
            >
              <div className="sc-subject-label">
                <span className="sc-subject-name">{t("english")}:</span>
              </div>
              <div className="sc-evaluation-options">
                {evaluationOptions.map((option) => (
                  <span
                    key={option.value}
                    className={`sc-evaluation-option ${
                      data.subjectEvaluation.English === option.value
                        ? "sc-selected"
                        : ""
                    }`}
                  >
                    {option.title}
                  </span>
                ))}
              </div>
            </div>

            {/* Arabic */}
            <div
              className={`sc-subject-row ${
                currentDirection == "rtl"
                  ? "sc-sections-border-right"
                  : "sc-sections-border-left"
              }`}
            >
              <div className="sc-subject-label">
                <span className="sc-subject-name">{t("arabic")}:</span>
              </div>
              <div className="sc-evaluation-options">
                {evaluationOptions.map((option) => (
                  <span
                    key={option.value}
                    className={`sc-evaluation-option ${
                      data.subjectEvaluation.Arabic === option.value
                        ? "sc-selected"
                        : ""
                    }`}
                  >
                    {option.title}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* General Behavior Section */}
        <section className={`sc-section `}>
          <h3 className="sc-section-title">{t("generalBehavior")}</h3>

          <div
            className={`sc-behavior-section ${
              currentDirection == "rtl"
                ? "sc-sections-border-right"
                : "sc-sections-border-left"
            } `}
          >
            <div className="sc-behavior-item">
              <span className="sc-behavior-label">{t("behavior")}:</span>
              <span className="sc-behavior-value">
                {data.behavior || t("notEvaluated")}
              </span>
            </div>

            <div className="sc-behavior-item">
              <span className="sc-behavior-label">{t("specialCases")}:</span>
              <span className="sc-behavior-value">
                {data.specialCases || t("notEvaluated")}
              </span>
            </div>

            <div className="sc-behavior-item">
              <span className="sc-behavior-label">{t("speaking")}:</span>
              <span className="sc-behavior-value">
                {data.speaking || t("notEvaluated")}
              </span>
            </div>

            <div className="sc-behavior-item">
              <span className="sc-behavior-label">
                {t("attentionAndFocus")}:
              </span>
              <span className="sc-behavior-value">
                {data.attentionAndFocus || t("notEvaluated")}
              </span>
            </div>
          </div>
        </section>

        {/* Notes Section */}
        {data.notes && (
          <section className="sc-section">
            <div
              className={`sc-notes  ${
                currentDirection == "rtl"
                  ? "sc-sections-border-right"
                  : "sc-sections-border-left"
              } `}
            >
              <h3 className="sc-notes-title">
                <img src={infoIcon} className="sc-info-icon" />
                {t("notes")}
              </h3>
              <p className="sc-notes-text">{data.notes}</p>
            </div>
          </section>
        )}
      </main>

      {/* Footer Section */}
      <footer className="sc-footer">
        <div className="sc-footer-content">
          <p className="sc-footer-title">{t("ehsiblySecondSlogan")}</p>
          <p className="sc-footer-subtitle">{t("alhabbarSlogan")}</p>
        </div>
      </footer>
    </div>
  );
});

StudentEvaluationTemplate.displayName = "StudentEvaluationTemplate";

export default StudentEvaluationTemplate;
